import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import env from "../config";
import { Account } from "../types";
import {
  Features,
  PLAN_FEATURES,
  PLAN_LABELS,
  Tier,
  normalizeTier,
} from "./plans";
import UpgradeModal from "./UpgradeModal";
import {
  authenticateWithGhl,
  getAppToken,
  startAutoRefresh,
} from "./auth";

interface MarketplaceCtx {
  // The single identifier the URL gives us. Everything else (companyId,
  // planId, features) is loaded from the Account row referenced by this id.
  accountId: string;
  account: Account | null;
  loading: boolean;
  notFound: boolean;
  // No verified session (strict mode, opened outside the CRM) — the dashboard
  // shows a "open from your CRM" gate instead of content.
  unauthenticated: boolean;

  // Plan + feature shortcuts. `features` is the backend's resolved entitlements
  // (account.features, resolved live from ghl_plan_id), falling back to the
  // local matrix by tier.
  planId: Tier;
  tier: Tier;
  tierLabel: string;
  features: Features;

  // Helpers
  buildPath: (sub: string) => string;
  refreshAccount: () => Promise<void>;

  // Upgrade modal
  openUpgrade: () => void;
  closeUpgrade: () => void;

  // Custom-domain default (verified) for short-link rendering.
  customDomain: string | null;
  refreshCustomDomain: () => Promise<void>;
}

const Ctx = createContext<MarketplaceCtx | null>(null);

const buildAccountUrl = (id: string) =>
  `${env.API_URL}/accounts/${encodeURIComponent(id)}`;

export const MarketplaceProvider = ({ children }: PropsWithChildren) => {
  const { accountId = "" } = useParams<{ accountId: string }>();

  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [unauthenticated, setUnauthenticated] = useState(false);
  const [customDomain, setCustomDomain] = useState<string | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const refreshAccount = useCallback(async () => {
    if (!accountId) {
      setAccount(null);
      setNotFound(true);
      setLoading(false);
      return;
    }
    const token = getAppToken();
    try {
      const { data } = await axios.get(buildAccountUrl(accountId), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const doc: Account | undefined = data?.data || data;
      if (doc && doc._id) {
        setAccount(doc);
        setNotFound(false);
        setUnauthenticated(false);
      } else {
        setAccount(null);
        setNotFound(true);
      }
    } catch (err: any) {
      const body = err?.response?.data;
      if (body?.unauthorized || err?.response?.status === 401) {
        // Strict mode with no valid session → prompt to open from the CRM.
        setAccount(null);
        setUnauthenticated(true);
        setNotFound(false);
      } else {
        setAccount(null);
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const refreshCustomDomain = useCallback(async () => {
    // The backend resolves accounts by the public `accountId` (see loadAccount),
    // NOT the Mongo _id — sending the wrong one returns no domains.
    if (!accountId) {
      setCustomDomain(null);
      return;
    }
    try {
      const token = getAppToken();
      const { data } = await axios.get(`${env.API_URL}/url_domains`, {
        headers: {
          "x-account-id": accountId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const list = data?.data || data || [];
      const def = Array.isArray(list)
        ? list.find((d: any) => d.is_default && d.verified)
        : null;
      setCustomDomain(def?.host || null);
    } catch {
      setCustomDomain(null);
    }
  }, [accountId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Safety net: if the dashboard loaded without a valid token (e.g. an
      // inner-frame reload after expiry), re-establish the session from GHL
      // before fetching the account. No-op (immediate) when not embedded.
      if (!getAppToken()) await authenticateWithGhl();
      if (!cancelled) refreshAccount();
    })();
    // Keep the session token fresh in the background for the app's lifetime.
    startAutoRefresh();
    return () => {
      cancelled = true;
    };
  }, [refreshAccount]);

  const planId: Tier = normalizeTier(account?.plan || account?.plan_id);
  const tier = planId;

  useEffect(() => {
    if (tier === "pro" || tier === "starter") refreshCustomDomain();
    else setCustomDomain(null);
  }, [tier, refreshCustomDomain]);

  const value = useMemo<MarketplaceCtx>(
    () => ({
      accountId,
      account,
      loading,
      notFound,
      unauthenticated,
      planId,
      tier,
      // Resolved by the backend (live from ghl_plan_id); local matrix is only a
      // fallback before the account loads.
      tierLabel: account?.plan_label || PLAN_LABELS[tier],
      features: (account?.features as Features) || PLAN_FEATURES[tier],
      buildPath: (sub: string) =>
        `/${accountId}/${sub.replace(/^\/+/, "")}`,
      refreshAccount,
      openUpgrade: () => setUpgradeOpen(true),
      closeUpgrade: () => setUpgradeOpen(false),
      customDomain,
      refreshCustomDomain,
    }),
    [
      accountId,
      account,
      loading,
      notFound,
      unauthenticated,
      planId,
      tier,
      customDomain,
      refreshAccount,
      refreshCustomDomain,
    ],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <UpgradeModal
        open={upgradeOpen}
        currentTier={tier}
        locationId={account?.accountId || accountId}
        onClose={() => setUpgradeOpen(false)}
      />
    </Ctx.Provider>
  );
};

export const useMarketplace = (): MarketplaceCtx => {
  const v = useContext(Ctx);
  if (!v)
    throw new Error("useMarketplace must be used inside MarketplaceProvider");
  return v;
};

// Non-throwing variant for code that may run outside the provider — e.g. the
// GHL OAuth callback under /integration, which has no account yet. Returns null
// when there's no MarketplaceProvider above.
export const useMarketplaceOptional = (): MarketplaceCtx | null =>
  useContext(Ctx);
